import { motion } from 'framer-motion'
import Link from '../components/LocalizedLink'
import { Zap, Target, Cpu, Download, ArrowRight, Wrench, BarChart2, Shield } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'
import SEO from '../components/SEO'
import { CountUp, useLiveStats } from '../components/LiveStats'

const translations = {
  el: {
    hero: {
      tag: 'Ποιοι Είμαστε',
      title: 'Μηχανικοί που Έζησαν το Πρόβλημα',
      subtitle: 'Μια ομάδα αδέσμευτων μηχανικών, φανατικών της αυτοματοποίησης και λάτρεις της βελτιστοποίησης που ζουν και αναπνέουν βαριές μηχανές — με δεκαετίες πρακτικής εμπειρίας στη βιομηχανία ελασμάτων.',
    },
    story: {
      title: 'Δεν γνωρίζουμε απλώς την παραγωγή — την ξέρουμε από πρώτο χέρι.',
      p1: 'Ξέρουμε καλά ότι στην κατεργασία λαμαρίνας, κάθε χιλιοστό και κάθε δευτερόλεπτο κοστίζει. Γι\' αυτόν τον λόγο, έχουμε αφιερώσει την καριέρα μας στο να εξαφανίζουμε τις χρονοβόρες, χειροκίνητες διαδικασίες και να τις μετατρέπουμε σε αυτοματοποιημένες, γρήγορες ροές εργασίας.',
      p2: 'Γεφυρώνουμε το χάσμα ανάμεσα στην παραδοσιακή βαριά βιομηχανία και το σύγχρονο λογισμικό αιχμής. Βοηθάμε τις κατασκευαστικές επιχειρήσεις να αναπτυχθούν, να μειώσουν τη φύρα των υλικών και να απογειώσουν την παραγωγικότητά τους.',
      quoteLabel: 'Η Φιλοσοφία μας',
      quote: '"Ό,τι μπορεί να βελτιστοποιηθεί, πρέπει να βελτιστοποιηθεί. Ό,τι μπορεί να αυτοματοποιηθεί, το κάνουμε πράξη."',
      stat1: 'Χρόνια στην Κατεργασία Λαμαρίνας',
      stat2: 'Προσφορές που Εκδόθηκαν',
    },
    values: {
      title: 'Τι μας Κινεί',
      subtitle: 'Οι αξίες πίσω από κάθε λειτουργία που κυκλοφορούμε.',
      items: [
        { title: 'Ακρίβεια Πρώτα', description: 'Κάθε χιλιοστό και κάθε δευτερόλεπτο μετράνε στο εργαστήριο. Τα εργαλεία μας αντικατοπτρίζουν αυτή την εμμονή με την ακρίβεια.' },
        { title: 'Αυτοματοποίηση Παντού', description: 'Αν μπορεί να βελτιστοποιηθεί, πρέπει να βελτιστοποιηθεί. Αν μπορεί να αυτοματοποιηθεί, το χτίζουμε.' },
        { title: 'Βάθος Μηχανικής', description: 'Γεφυρώνουμε παραδοσιακή βαριά μηχανική και σύγχρονο λογισμικό — χωρίς επιφανειακές λύσεις.' },
        { title: 'Πραγματικά Αποτελέσματα', description: 'Τα κριτήρια μας προέρχονται από το εργοστάσιο, όχι από whiteboard. Κάθε λειτουργία λύνει ένα πραγματικό πρόβλημα.' },
        { title: 'Χτισμένο για να Διαρκεί', description: 'Desktop πρώτα, λειτουργία χωρίς internet, χωρίς απαιτητικές συνδρομές. Αξιόπιστο όταν το χρειάζεστε περισσότερο.' },
        { title: 'Βετεράνοι της Βιομηχανίας', description: 'Δεκαετίες συλλογικής πρακτικής εμπειρίας σε ελάσματα — έχουμε δουλέψει τα μηχανήματα, όχι απλώς τα έχουμε μοντελοποιήσει.' },
      ],
    },
    app: {
      tag: 'Η Εφαρμογή',
      title: 'Κουραστήκατε να Προσφέρετε στο Σκοτάδι;',
      p1: 'Περάσαμε χρόνια στο εργαστήριο αντιμετωπίζοντας τα ίδια ακριβώς προβλήματα που αντιμετωπίζετε κάθε μέρα: αργοί κύκλοι προσφορών, ανακριβείς εκτιμήσεις υλικών και χαμένα κέρδη. Οπότε χτίσαμε τη βέλτιστη λύση.',
      bullets: [
        { label: 'Αποδεδειγμένη Τεχνογνωσία', detail: 'Χτισμένο με δεκαετίες πρακτικής εμπειρίας στη βιομηχανία ελάσματος.' },
        { label: 'Άψογη Αυτοματοποίηση', detail: 'Αποχαιρετήστε τα κουραστικά υπολογιστικά φύλλα και τους χειροκίνητους υπολογισμούς.' },
        { label: 'Μηχανική Ακρίβεια', detail: 'Εξαιρετικά ακριβείς εκτιμήσεις κόστους για υλικά, κάμψη, laser και εργατικά — σε δευτερόλεπτα.' },
      ],
    },
    cta: {
      title: 'Σταματήστε να Μαντεύετε. Κερδίστε Περισσότερες Προσφορές.',
      subtitle: 'Ξεκινήστε τη δωρεάν δοκιμή σήμερα — 5 ημέρες δωρεάν, χωρίς πιστωτική κάρτα.',
      btn1: 'Δωρεάν Δοκιμή',
      btn2: 'Δείτε Λειτουργίες',
    },
  },
  en: {
    hero: {
      tag: 'Who We Are',
      title: 'Engineers Who\'ve Lived the Problem',
      subtitle: 'A team of relentless engineers, automation fanatics, and optimization geeks who live and breathe heavy machinery — with decades of hands-on experience in the sheet metal industry.',
    },
    story: {
      title: 'We Don\'t Just Understand the Shop Floor — We\'ve Mastered It',
      p1: 'We know that in manufacturing, every millimeter and every second counts. That\'s why we\'ve dedicated our careers to transforming complex, manual bottlenecks into streamlined, automated workflows.',
      p2: 'We bridge the gap between traditional heavy engineering and cutting-edge software to help manufacturing businesses scale effortlessly, reduce waste, and maximize throughput.',
      quoteLabel: 'Our Philosophy',
      quote: '"If it can be optimized, it should be. If it can be automated, we build it."',
      stat1: 'Years in Sheet Metal',
      stat2: 'Quotes Generated',
    },
    values: {
      title: 'What Drives Us',
      subtitle: 'The values behind every feature we ship.',
      items: [
        { title: 'Precision First', description: 'Every millimeter and every second counts on the shop floor. Our tools reflect that obsession with accuracy.' },
        { title: 'Automate Everything', description: 'If it can be optimized, it should be. If it can be automated, we build it.' },
        { title: 'Engineering Depth', description: 'We bridge traditional heavy engineering and cutting-edge software — no superficial SaaS thinking.' },
        { title: 'Real-World Results', description: 'Our benchmarks come from the factory floor, not a whiteboard. Every feature solves a real problem.' },
        { title: 'Built to Last', description: 'Desktop-first, offline-capable, no subscriptions required for core use. Reliable when you need it most.' },
        { title: 'Industry Veterans', description: 'Decades of collective hands-on experience in sheet metal — we\'ve worked the machines, not just modeled them.' },
      ],
    },
    app: {
      tag: 'The App',
      title: 'Tired of Quoting in the Dark?',
      p1: 'We spent years on the shop floor dealing with the exact same headaches you face every day: slow quoting cycles, inaccurate material estimates, and lost profits. So we built the ultimate solution.',
      bullets: [
        { label: 'Industry-Proven Expertise', detail: 'Built with decades of real-world sheet metal manufacturing experience.' },
        { label: 'Flawless Automation', detail: 'Say goodbye to tedious spreadsheets and manual math.' },
        { label: 'Precision Engineering', detail: 'Hyper-accurate cost estimates for materials, bending, laser cutting, and labor — in seconds.' },
      ],
    },
    cta: {
      title: 'Stop Guessing. Start Winning More Bids.',
      subtitle: 'Start your free trial today — 5 days free, no credit card required.',
      btn1: 'Download Free Trial',
      btn2: 'Explore Features',
    },
  },
}

const valueIcons = [
  <Target className="h-7 w-7" />,
  <Zap className="h-7 w-7" />,
  <Cpu className="h-7 w-7" />,
  <BarChart2 className="h-7 w-7" />,
  <Shield className="h-7 w-7" />,
  <Wrench className="h-7 w-7" />,
]

const About = () => {
  const { lang } = useLanguage()
  const stats = useLiveStats()
  const t = translations[lang]

  return (
    <div className="overflow-hidden">
      <SEO
        title="About Us"
        description="Learn about Workshop Assistant, the sheet metal and CNC router cost estimation software built for fabrication shops."
        path="/about"
      />
      {/* Hero */}
      <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="section-container">
          <div className="max-w-3xl mx-auto text-center">
            <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-primary-400 font-semibold uppercase tracking-widest text-sm mb-4">{t.hero.tag}</motion.p>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              {t.hero.title.split('\'').map((part, i, arr) => i < arr.length - 1 ? [part, '\''] : part).flat().join('')}
              <span className="text-primary-400"></span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-xl text-gray-300 leading-relaxed">{t.hero.subtitle}</motion.p>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="section-container bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <h2 className="text-4xl font-bold mb-6">{t.story.title}</h2>
              <div className="space-y-5 text-gray-600 text-lg leading-relaxed">
                <p>{t.story.p1}</p>
                <p>{t.story.p2}</p>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="bg-gradient-to-br from-primary-600 to-primary-800 rounded-2xl p-8 text-white">
              <div className="text-primary-200 text-sm font-semibold uppercase tracking-widest mb-4">{t.story.quoteLabel}</div>
              <blockquote className="text-2xl font-bold leading-snug mb-6">{t.story.quote}</blockquote>
              <div className="border-t border-primary-500 pt-6 grid grid-cols-2 gap-6 text-center">
                <div><div className="text-4xl font-bold">10+</div><div className="text-primary-200 text-sm mt-1">{t.story.stat1}</div></div>
                <div><div className="text-4xl font-bold tabular-nums"><CountUp value={stats?.offers} lang={lang} /></div><div className="text-primary-200 text-sm mt-1">{t.story.stat2}</div></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-container bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold mb-4">{t.values.title}</h2>
            <p className="text-xl text-gray-600">{t.values.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.values.items.map((v, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }}
                className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md hover:border-primary-300 transition-all duration-200">
                <div className="w-12 h-12 bg-primary-100 text-primary-600 rounded-xl flex items-center justify-center mb-4">{valueIcons[i]}</div>
                <h3 className="font-semibold text-gray-900 text-lg mb-2">{v.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{v.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* App flyer section */}
      <section className="section-container bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <p className="text-primary-600 font-semibold uppercase tracking-widest text-sm mb-3">{t.app.tag}</p>
              <h2 className="text-4xl font-bold mb-6">{t.app.title}</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">{t.app.p1}</p>
              <ul className="space-y-4">
                {t.app.bullets.map((item, i) => (
                  <li key={i} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mt-0.5">
                      <svg className="w-4 h-4 text-green-600" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor"><path d="M5 13l4 4L19 7" /></svg>
                    </div>
                    <div><span className="font-semibold text-gray-900">{item.label}:</span>{' '}<span className="text-gray-600">{item.detail}</span></div>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="rounded-2xl overflow-hidden shadow-2xl border border-gray-200">
              <img src={`${import.meta.env.BASE_URL}screenshots/calculator.png`} alt="Workshop Assistant cost calculator interface" className="w-full h-auto block" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-container bg-gradient-to-br from-primary-600 to-primary-800 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-4">{t.cta.title}</h2>
          <p className="text-xl opacity-90 mb-8">{t.cta.subtitle}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/download" className="inline-flex items-center justify-center space-x-2 px-8 py-4 bg-white text-primary-600 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-200 shadow-lg">
              <Download className="h-5 w-5" /><span>{t.cta.btn1}</span>
            </Link>
            <Link to="/features" className="inline-flex items-center justify-center space-x-2 px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-all duration-200">
              <span>{t.cta.btn2}</span><ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}

export default About
